import React from "react";
import { useForm } from "react-hook-form";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { FormHelpCallout } from "../FormHelpCallout";
import { calculateExpenseAtMonth } from "../../../utils/chart/scaling.utils";
import { safeDate } from "../../../utils/date";
import type { ReviseExpenseData } from "../../../services/api/financial.service";
import type { Expense } from "../../../types/startup.types";

export interface ExpenseRevisionFormProps {
  expense: Expense;
  defaultEffectiveDate?: string;
  contextMonthlyRevenue?: number;
  onSubmit: (data: ReviseExpenseData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface ExpenseRevisionFormValues {
  effectiveDate: string;
  monthlyAmount: number;
  growthRate: number;
  revenueRatio: number;
  revisionNote?: string;
}

function toExpenseForCalculation(expense: Expense) {
  return {
    ...expense,
    amount: expense.monthlyAmount ?? expense.amount ?? 0,
    frequency: expense.frequency === "one-time" ? ("monthly" as const) : expense.frequency,
  };
}

export const ExpenseRevisionForm: React.FC<ExpenseRevisionFormProps> = ({
  expense,
  defaultEffectiveDate,
  contextMonthlyRevenue = 0,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const initialEffectiveDate =
    defaultEffectiveDate || new Date().toISOString().split("T")[0];
  const expenseType = expense.expenseType || "fixed";

  const modeledAmount = React.useMemo(() => {
    const asOf = safeDate(initialEffectiveDate);
    return calculateExpenseAtMonth(
      toExpenseForCalculation(expense),
      asOf,
      contextMonthlyRevenue,
    );
  }, [expense, initialEffectiveDate, contextMonthlyRevenue]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ExpenseRevisionFormValues>({
    defaultValues: {
      effectiveDate: initialEffectiveDate,
      monthlyAmount: Math.round(modeledAmount * 100) / 100,
      growthRate: expense.growthRate ? expense.growthRate * 100 : 0,
      revenueRatio: expense.revenueRatio ? expense.revenueRatio * 100 : 0,
      revisionNote: "",
    },
  });

  const effectiveDate = watch("effectiveDate");
  const modeledAsOfDate = React.useMemo(() => {
    const asOf = safeDate(effectiveDate);
    if (!asOf) return modeledAmount;
    return calculateExpenseAtMonth(
      toExpenseForCalculation(expense),
      asOf,
      contextMonthlyRevenue,
    );
  }, [expense, effectiveDate, contextMonthlyRevenue, modeledAmount]);

  const handleFormSubmit = (data: ExpenseRevisionFormValues) => {
    onSubmit({
      effectiveDate: data.effectiveDate,
      monthlyAmount:
        expenseType === "percentage" ? undefined : Number(data.monthlyAmount),
      growthRate: Number(data.growthRate) / 100,
      revenueRatio:
        expenseType === "percentage" || expenseType === "hybrid"
          ? Number(data.revenueRatio) / 100
          : undefined,
      revisionNote: data.revisionNote?.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <FormHelpCallout>
        Revise <strong>{expense.name}</strong> from a date forward. Past months
        stay as originally modeled.
      </FormHelpCallout>

      <FormField label="Effective date" error={errors.effectiveDate?.message}>
        <Input
          type="date"
          {...register("effectiveDate", {
            required: "Effective date is required",
          })}
        />
      </FormField>

      <div className="rounded-[14px] border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
        Modeled monthly amount as of {effectiveDate || initialEffectiveDate}:{" "}
        <span className="font-medium text-white">
          ${modeledAsOfDate.toLocaleString(undefined, { maximumFractionDigits: 0 })}
        </span>
      </div>

      {expenseType !== "percentage" ? (
        <FormField
          label="New monthly amount"
          error={errors.monthlyAmount?.message}
        >
          <Input
            type="number"
            step="0.01"
            {...register("monthlyAmount", {
              required: "Monthly amount is required",
              min: { value: 0, message: "Amount must be positive" },
              valueAsNumber: true,
            })}
          />
        </FormField>
      ) : null}

      {expenseType === "percentage" || expenseType === "hybrid" ? (
        <FormField
          label="New revenue ratio (%)"
          error={errors.revenueRatio?.message}
        >
          <Input
            type="number"
            step="0.01"
            {...register("revenueRatio", {
              required: "Revenue ratio is required",
              valueAsNumber: true,
            })}
          />
        </FormField>
      ) : null}

      <FormField
        label="New monthly growth rate (%)"
        error={errors.growthRate?.message}
        helperText="Resets from the effective date."
      >
        <Input
          type="number"
          step="0.01"
          {...register("growthRate", { valueAsNumber: true })}
        />
      </FormField>

      <FormField label="Note (optional)" error={errors.revisionNote?.message}>
        <Input
          {...register("revisionNote")}
          placeholder="e.g. rent increase, refinanced"
        />
      </FormField>

      <div className="flex gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          size="md"
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? "Saving revision..." : "Save revision"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default ExpenseRevisionForm;
